import {
  faUsers,
  faEnvelope,
  faTrash,
  faUserPlus,
  faUserCircle,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import ConfirmationDialog from '../components/ConfirmationDialog';
import NotificationModal from '../components/NotificationModal';
import StatsCard from '../components/StatsCard';
import useUsers from '../hooks/useUsers';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import { ROLE_COLORS, ROLE_DISPLAY_NAMES, UserRole } from '../services/invitationService';
import Badge from '../components/Badge';
import InviteUserModal from '../components/InviteUserModal';
import ChangeUserRoleModal from '../components/ChangeUserRoleModal';
import { useAuth } from '../hooks/useAuth';

interface NotificationState {
  type: 'success' | 'error';
  title: string;
  message: string;
}

export default function Users() {
  const { user: authUser, currentOrganization } = useAuth();
  const { users, loading, error, listUsers, removeUser, changeUserRole } = useUsers();
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<any>(null);
  const [notification, setNotification] = useState<NotificationState | null>(null);

  useEffect(() => {
    listUsers(1);
  }, [listUsers]);

  const handleDeleteUser = async (userId: string): Promise<void> => {
    try {
      await removeUser(userId);
      setDeleteUserId(null);
      setNotification({
        type: 'success',
        title: 'Usuário Removido',
        message: 'O usuário foi removido com sucesso do sistema.'
      });
      await listUsers(1);
    } catch (error) {
      setDeleteUserId(null);
      setNotification({
        type: 'error',
        title: 'Erro ao Remover Usuário',
        message: (error as Error).message || 'Ocorreu um erro inesperado. Tente novamente.'
      });
    }
  };

  const handleInviteSuccess = (): void => {
    setNotification({
      type: 'success',
      title: 'Sucesso!',
      message:
        'Convite enviado com sucesso! O usuário receberá um email com as instruções para acessar o sistema.'
    });
    listUsers(1);
  };

  const handleOpenRoleModal = (user: any): void => {
    setSelectedUserForRole(user);
    setShowRoleModal(true);
  };

  const handleRoleChangeSuccess = async (userId: string, newRole: UserRole): Promise<void> => {
    const result = await changeUserRole(userId, newRole);
    if (result.success) {
      setNotification({
        type: 'success',
        title: 'Função Alterada',
        message: 'A função do usuário foi alterada com sucesso.'
      });
    } else {
      throw new Error(result.error || 'Erro ao alterar função do usuário');
    }
  };

  if (error) {
    return (
      <main className="w-full flex-grow p-6">
        <div className="text-center py-12">
          <p className="text-red-600">Erro ao carregar usuários: {error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full flex-grow p-6">
      <div className="mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Gerenciar Usuários</h1>
          <p className="text-gray-600">Administração de usuários da clínica</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatsCard
            title="Total de Usuários"
            value={loading ? '...' : users?.length || 0}
            icon={faUsers}
            color="blue"
          />
          <StatsCard
            title="Usuários Ativos"
            value={loading ? '...' : users?.length || 0}
            icon={faUserCircle}
            color="green"
          />
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <Button
              onClick={() => setShowInviteModal(true)}
              variant="primary"
              size="lg"
              fullWidth
              leftIcon={faUserPlus}
            >
              Convidar Usuário
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Lista de Usuários</h3>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <img className="w-12 h-12" src="/loading.svg" alt="loading" />
            </div>
          ) : users && users.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {users.map(user => (
                <div
                  key={user.id}
                  className="p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <FontAwesomeIcon icon={faUserCircle} className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-medium text-gray-900">
                          {user.name || 'Convite pendente'}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge
                            text={ROLE_DISPLAY_NAMES[user.role as UserRole]}
                            color={ROLE_COLORS[user.role as UserRole]}
                          />
                          <IconButton
                            onClick={() => handleOpenRoleModal(user)}
                            icon={faEdit}
                            variant="primary"
                            size="sm"
                            tooltip="Alterar função"
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4" />
                        <span>{user.email}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <IconButton
                      onClick={() => setDeleteUserId(user.id)}
                      icon={faTrash}
                      variant="danger"
                      tooltip={
                        user.id === authUser?.id
                          ? 'Você não pode remover a si mesmo'
                          : 'Remover usuário'
                      }
                      disabled={user.id === authUser?.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhum usuário encontrado</p>
              <Button onClick={() => setShowInviteModal(true)} variant="outline" className="mt-4">
                Convidar primeiro usuário
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Invite User Modal */}
      {currentOrganization && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          organization={currentOrganization}
          onInviteSuccess={handleInviteSuccess}
        />
      )}

      {/* Change User Role Modal */}
      <ChangeUserRoleModal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedUserForRole(null);
        }}
        user={selectedUserForRole}
        currentUserId={authUser?.id}
        onRoleChangeSuccess={handleRoleChangeSuccess}
      />

      {deleteUserId && (
        <ConfirmationDialog
          title="Remover Usuário"
          message="Tem certeza que deseja remover este usuário? Esta ação não pode ser desfeita."
          onConfirm={() => handleDeleteUser(deleteUserId)}
          onCancel={() => setDeleteUserId(null)}
          confirmText="Remover"
          cancelText="Cancelar"
          type="danger"
        />
      )}

      {notification && (
        <NotificationModal
          title={notification.title}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </main>
  );
}
